import { XsI18nPipe } from '@nx-fe/pipes';
import { FeatureToggleService } from '@nx-fe/services';
import { ShortDateFormatPipe } from 'app/shared/pipes/time-format.pipe';
import { HtmlTreeNode, TreeNode } from './htmlTreeNode';

export interface IHtmlTreeDelegate {
  toggle: (node: TreeNode) => void;
  exploreTo: (node: TreeNode) => void;
  clickNode: (node: TreeNode) => void;
  onChangeSelectedNode: (nodes: TreeNode) => void;
  breadCrumbNodes: TreeNode[];
}

export class HtmlTree {
  public htmlNodeMap = new Map();
  public currentId = 1;
  public rootNode: TreeNode;
  public viewRootNode: TreeNode;
  public transferViewRootNode: TreeNode;

  constructor(
    private fileExplorerState: IHtmlTreeDelegate,
    private alwaysTooltip: boolean,
    private saI18nPipe: XsI18nPipe,
    private dateFormatPipe: ShortDateFormatPipe,
    private featureToggleService: FeatureToggleService
  ) {}

  /** Selectively clones this HtmlTree for rc state transfer purposes */
  public static cloneForTransfer = (
    oldTree: HtmlTree,
    fileExplorerState: IHtmlTreeDelegate,
    saI18nPipe: XsI18nPipe,
    dateFormatPipe: ShortDateFormatPipe,
    featureToggleService: FeatureToggleService
  ): HtmlTree => {
    const newTree = new HtmlTree(
      fileExplorerState,
      oldTree.alwaysTooltip,
      saI18nPipe,
      dateFormatPipe,
      featureToggleService
    );

    // Don't copy dom nodes as they are not used for transfer purposes
    newTree.currentId = oldTree.currentId;

    if (oldTree.rootNode) {
      newTree.rootNode = TreeNode.cloneForTransfer(oldTree.rootNode);
    }

    // Find the viewRootNode in the new hierarchy
    if (oldTree.viewRootNode) {
      newTree.viewRootNode = TreeNode.findNodeById(newTree.rootNode, oldTree.viewRootNode.id);
    }

    return newTree;
  };

  /**
   * Called after the file browser has been made visible to complete the transfer,
   * by traversing the tree to create a visual representation (HTML/DOM elements).
   */
  public transfer = (data: TreeNode[], node: TreeNode, viewRootNode: TreeNode, element: HTMLElement) => {
    node.nodes = [];
    for (const curNode of data) {
      const newNode = new TreeNode();
      if (curNode.id === viewRootNode.id) {
        this.transferViewRootNode = newNode;
      }
      newNode.id = this.currentId++;
      newNode.title = curNode.title;
      newNode.tooltipText = curNode.tooltipText;
      newNode.nodes = undefined;
      newNode.transferNodes = curNode.nodes;
      newNode.parent = node;
      newNode.data = curNode.data;
      newNode.level = curNode.level;
      newNode.type = curNode.type;
      newNode.collapsed = curNode.collapsed;
      newNode.isDownloading = false;
      newNode.percentValue = 0;
      node.nodes.push(newNode);
    }
    this.traverse(node, element);
    for (const curNode of node.nodes) {
      if (curNode.transferNodes && curNode.transferNodes.length > 0) {
        curNode.nodes = curNode.transferNodes;
        this.transfer(curNode.nodes, curNode, viewRootNode, element);
        if (!curNode.collapsed) {
          this.htmlNodeMap.get(curNode.id).expandNode();
        } else {
          this.htmlNodeMap.get(curNode.id).collapseNode();
        }

        curNode.transferNodes = undefined;
      }
    }
  };

  public populateNodes = (data: TreeNode[], node: TreeNode, element: HTMLElement) => {
    for (const x of data) {
      x.level = node.level + 1;
      x.collapsed = true;
      x.id = this.currentId++;
      x.parent = node;
      x.tooltipText = x.data.fullPath;
    }
    node.nodes = data;
    this.traverse(node, element);
  };

  public concatPopulateNodes = (data: TreeNode[], node: TreeNode, element: HTMLElement) => {
    for (const x of data) {
      x.level = node.level + 1;
      x.collapsed = true;
      x.id = this.currentId++;
      x.parent = node;
      x.tooltipText = x.data.fullPath;
    }
    node.nodes = node.nodes.concat(data);
    this.traverse(node, element);
  };

  public isEmpty = () => this.viewRootNode.nodes.length === 0;

  public isVisibleNodeInTree = (node: TreeNode) => {
    if (!node) {
      return false;
    }
    let parentPointer = node.parent;
    while (parentPointer && parentPointer != this.viewRootNode) {
      if (parentPointer.collapsed) {
        return false;
      }
      parentPointer = parentPointer.parent;
    }

    return parentPointer === this.viewRootNode;
  };

  private traverse = (node: TreeNode, element: HTMLElement) => {
    let htmlNode: HtmlTreeNode;
    let appendNodes = true;
    if (this.htmlNodeMap.has(node.id)) {
      htmlNode = this.htmlNodeMap.get(node.id);
      appendNodes = false;
    } else {
      htmlNode = new HtmlTreeNode(
        node,
        this.alwaysTooltip,
        this.fileExplorerState,
        this.saI18nPipe,
        this.dateFormatPipe,
        this.featureToggleService
      );
      this.htmlNodeMap.set(node.id, htmlNode);
    }

    htmlNode.namePrefixNode.onclick = (event: Event) => {
      htmlNode.toggleNode();
      this.fileExplorerState.toggle(node);
      event.stopPropagation();
    };

    htmlNode.rowNode.ondblclick = (event: Event) => {
      event.stopPropagation();
    };

    if (
      this.featureToggleService.isEnabled('FileManagerActionBarRenameDownloadDelete') ||
      this.featureToggleService.isEnabled('FileManagerActionBarCopyAttachCompressMove')
    ) {
      htmlNode.rowNode.onclick = (event: Event) => {
        this.fileExplorerState.onChangeSelectedNode(node);
        event.stopPropagation();
      };

      htmlNode.nameTextTitle.onclick = (event: Event) => {
        if (!htmlNode.isLoading()) {
          this.fileExplorerState.clickNode(node);
        }
        event.stopPropagation();
      };
    } else {
      htmlNode.nameTextNode.onclick = () => {
        if (!htmlNode.isLoading()) {
          this.fileExplorerState.clickNode(node);
        }
      };
    }

    if (node.nodes) {
      for (const childNode of node.nodes) {
        this.traverse(childNode, htmlNode.childNodesTable);
      }
    }

    if (appendNodes) {
      element.appendChild(htmlNode.rowNode);
    }
  };
}
